// Every `@velocity-exchange/common` preset, plus the entries only this app needs.
import {
	PRESETS as COMMON_PRESETS,
	type AbbreviateOptions,
	type FormatOptions,
	type SmallNumberOptions,
} from '@velocity-exchange/common/format';
import { BigNum } from '@velocity-exchange/sdk';

// The common package doesn't export its `deepFreeze`, so the presets below get one.
function deepFreeze<T>(value: T): T {
	if (value === null || typeof value !== 'object') return value;

	Object.freeze(value);
	for (const key of Object.getOwnPropertyNames(value)) {
		deepFreeze((value as Record<string, unknown>)[key]);
	}

	return value;
}

/*
 * Legacy number display always rendered a small value as a subscript, so every
 * preset and bag below that replaces one carries this. It only shows once the
 * rounded value has more than three leading zeros, which needs five decimals or
 * more, so it is inert on the fixed two-decimal presets and decides the output
 * on anything whose decimals come from a market.
 */
const SUBSCRIPT: SmallNumberOptions = {
	mode: 'subscript',
	order: 'after-digits',
};

/** What `<NumberDisplay millify />` abbreviated to before the format layer. */
const MILLIFY_LEGACY: AbbreviateOptions = {
	threshold: 1000,
	digits: { kind: 'significant', significant: 3, rounding: 'truncate' },
	trimTrailingZeros: false,
	overflow: 'full',
};

/**
 * What `BigNum.toNotional(true)` renders, except at precision 0, where BigNum
 * truncates the integer string and no call site formats.
 */
const usdTradePrecision: FormatOptions = deepFreeze({
	style: 'currency',
	digits: { kind: 'significant', significant: 6, rounding: 'truncate' },
	trimTrailingZeros: true,
});

/**
 * `millifiedAmount` with the dollar sign the call sites prefix by hand. A
 * negative reads as -$1.50 where the hand-written template gives $-1.50.
 */
const usdMillified: FormatOptions = deepFreeze({
	...COMMON_PRESETS.millifiedAmount,
	style: 'currency',
	sentinels: [{ matches: (value) => value.sign === 0, text: '$0' }],
	invalidText: '$0',
	nonFiniteText: { positive: '$0', negative: '$0' },
});

/** A bare number with every digit it carries, small values subscripted. */
const plainSubscript: FormatOptions = deepFreeze({
	digits: { kind: 'exact' },
	small: SUBSCRIPT,
});

/** Two decimals truncated, where `PRESETS.percent` rounds half up. */
const percentTruncate: FormatOptions = deepFreeze({
	style: 'percent',
	digits: { kind: 'decimals', decimals: 2, rounding: 'truncate' },
	small: SUBSCRIPT,
});

const percentTruncateTrim: FormatOptions = deepFreeze({
	...percentTruncate,
	trimTrailingZeros: true,
});

const percentSignedTruncate: FormatOptions = deepFreeze({
	...percentTruncate,
	signDisplay: 'exceptZero',
});

/**
 * Six significant figures truncated, what `precision="trade"` rendered. For a
 * value below one, `legacyTrim` picks the trimmed variant instead.
 */
const tradePrecisionLegacy: FormatOptions = deepFreeze({
	digits: {
		kind: 'significant',
		significant: 6,
		rounding: 'truncate',
		trailingZeros: 'trim',
	},
	small: SUBSCRIPT,
});

const tradePrecisionTrim: FormatOptions = deepFreeze({
	...tradePrecisionLegacy,
	trimTrailingZeros: true,
});

const usdTradePrecisionLegacy: FormatOptions = deepFreeze({
	...tradePrecisionLegacy,
	style: 'currency',
});

const usdTradePrecisionTrim: FormatOptions = deepFreeze({
	...tradePrecisionTrim,
	style: 'currency',
});

const TRIMMED_BELOW_ONE = new Map<FormatOptions, FormatOptions>([
	[tradePrecisionLegacy, tradePrecisionTrim],
	[usdTradePrecisionLegacy, usdTradePrecisionTrim],
]);

/** Significant digits never pad a value below one, so the legacy props trimmed its zeroes. */
export function legacyTrim(
	base: FormatOptions,
	value: BigNum | null | undefined
): FormatOptions {
	if (!value || value.eqZero()) return base;

	const belowOne = value.abs().lt(BigNum.fromPrint('1', value.precision));

	return belowOne ? (TRIMMED_BELOW_ONE.get(base) ?? base) : base;
}

/** A bare number abbreviated at three significant figures from 1,000 up. */
const millifiedNumber: FormatOptions = deepFreeze({
	...plainSubscript,
	abbreviate: MILLIFY_LEGACY,
});

/** A token amount at three significant figures, abbreviated from 1,000 up. */
const millifiedTokenAmount: FormatOptions = deepFreeze({
	digits: {
		kind: 'significant',
		significant: 3,
		rounding: 'truncate',
		trailingZeros: 'trim',
	},
	small: SUBSCRIPT,
	abbreviate: MILLIFY_LEGACY,
});

/** Dollars cut at the cent, never rounded up. The base for a price at market decimals. */
const usdTruncate: FormatOptions = deepFreeze({
	style: 'currency',
	digits: { kind: 'decimals', decimals: 2, rounding: 'truncate' },
});

const usdMillifiedLegacy: FormatOptions = deepFreeze({
	...usdTruncate,
	abbreviate: MILLIFY_LEGACY,
	small: SUBSCRIPT,
});

const withDecimalsCache = new WeakMap<
	FormatOptions,
	Map<number, FormatOptions>
>();

/**
 * A preset at a decimals count only known at runtime, subscript included.
 * A market's price decimals reach five and six, where the subscript decides
 * whether a dust price reads as $0.0₄1 or as $0.00001.
 */
export function withDecimals(
	base: FormatOptions,
	decimals: number
): FormatOptions {
	// One object per base and count, so a memo keyed on options identity still hits.
	let byDecimals = withDecimalsCache.get(base);

	if (!byDecimals) {
		byDecimals = new Map();
		withDecimalsCache.set(base, byDecimals);
	}

	let options = byDecimals.get(decimals);

	if (!options) {
		// A bad count keeps the base's own digits, as the legacy props did.
		const valid = Number.isInteger(decimals) && decimals >= 0;

		options = deepFreeze({
			...base,
			...(valid
				? { digits: { kind: 'decimals', decimals, rounding: 'truncate' } }
				: {}),
			small: SUBSCRIPT,
		} as FormatOptions);
		byDecimals.set(decimals, options);
	}

	return options;
}

export const PRESETS = Object.freeze({
	...COMMON_PRESETS,
	usdTruncate,
	usdTradePrecision,
	usdMillified,
	plainSubscript,
	percentTruncate,
	percentTruncateTrim,
	percentSignedTruncate,
	tradePrecisionLegacy,
	tradePrecisionTrim,
	usdTradePrecisionLegacy,
	usdTradePrecisionTrim,
	millifiedNumber,
	millifiedTokenAmount,
	usdMillifiedLegacy,
});
